import React from 'react';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="text-navy text-[22px] font-black tracking-tight">{title}</h2>
    <div className="mt-3 space-y-3 text-body-muted text-[15px] leading-[1.8]">{children}</div>
  </section>
);

const TH = ({ children }) => (
  <th className="text-left text-body-muted text-[11px] font-bold uppercase tracking-[0.14em] px-4 py-3 border-b border-slate-200 bg-brand-soft">{children}</th>
);
const TD = ({ children }) => (
  <td className="text-navy text-[14px] px-4 py-3 border-b border-slate-100 align-top">{children}</td>
);

const Privacy = () => {
  return (
    <div className="min-h-screen bg-white font-sans text-navy">
      <Navbar />
      <main className="pt-28 pb-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <p className="text-brand-blue text-[12px] font-bold tracking-[0.16em] uppercase mb-4">Legal</p>
          <h1 className="text-navy font-black tracking-[-0.01em] leading-[1.08] text-[36px] sm:text-[44px]">Privacy Policy</h1>
          <p className="mt-4 text-body-muted text-[14px]">Last updated: 17 August 2026</p>

          <Section title="Who we are">
            <p>5DBS is a revenue intelligence platform for B2B publishers. This privacy policy explains how we collect, use, and protect personal data when you visit our website, download our resources, or contact us directly.</p>
            <p>For information about how Enrich IQ or AALIGN processes data, please see their respective privacy policies.</p>
          </Section>

          <Section title="What we collect">
            <p>We collect personal data in three scenarios:</p>
            <p className="text-navy font-bold">1. Website visitors</p>
            <p>Anonymised page views, referrers, and approximate location via privacy-respecting analytics (no cookies that track individual users).</p>
            <p className="text-navy font-bold">2. Report / resource downloads</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Name</li>
              <li>Work email address</li>
              <li>Company name (optional)</li>
            </ul>
            <p className="text-navy font-bold">3. Contact requests</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Name</li>
              <li>Work email address</li>
              <li>Company name (optional)</li>
              <li>Message content</li>
            </ul>
            <p>We do not collect cookies for marketing, tracking, or profiling. Our analytics are cookieless and anonymised.</p>
          </Section>

          <Section title="Why we use it (lawful basis under GDPR / CCPA)">
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="min-w-full">
                <thead><tr><TH>What</TH><TH>Why</TH><TH>Legal basis</TH></tr></thead>
                <tbody>
                  <tr><TD>Website analytics</TD><TD>To understand visitor patterns and improve our site</TD><TD>Legitimate interests</TD></tr>
                  <tr><TD>Report delivery</TD><TD>To send you the resource you requested</TD><TD>Performance of your request (contract)</TD></tr>
                  <tr><TD>Contact replies</TD><TD>To respond to your enquiry</TD><TD>Performance of your request (contract)</TD></tr>
                  <tr><TD>Marketing emails</TD><TD>To send relevant content, case studies, and updates</TD><TD>Your consent (opt-in)</TD></tr>
                  <tr><TD>Security</TD><TD>To prevent fraud and unauthorised access</TD><TD>Legitimate interests</TD></tr>
                </tbody>
              </table>
            </div>
          </Section>

          <Section title="Marketing emails — opt-in only">
            <ul className="list-disc pl-5 space-y-2">
              <li>You will only receive marketing emails if you check the optional "Yes, send me updates" box when downloading a resource.</li>
              <li>You will receive approximately 1–2 emails per week covering content, case studies, product updates, and related topics.</li>
              <li>Every email includes a one-click unsubscribe link. You can opt out anytime.</li>
              <li>We do not share your email with anyone. Ever.</li>
              <li>If you unsubscribe, we remove you from our list within 24 hours.</li>
            </ul>
          </Section>

          <Section title="Your rights">
            <p>Under GDPR, UK GDPR, and CCPA, you have the right to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Know</strong> — what data we hold on you</li>
              <li><strong>Correct</strong> — inaccurate data</li>
              <li><strong>Delete</strong> — your data (we'll remove it within 30 days)</li>
              <li><strong>Restrict</strong> — how we use your data</li>
              <li><strong>Receive</strong> — a copy of your data in portable format</li>
              <li><strong>Object</strong> — to us processing your data</li>
              <li><strong>Withdraw consent</strong> — from marketing emails anytime (one click)</li>
            </ul>
            <p>To exercise any of these rights, or to complain: <a href="mailto:privacy@fivedbs.com" className="text-brand-blue font-semibold hover:underline">privacy@fivedbs.com</a>.</p>
            <p>If you are in the EU or UK, you also have the right to lodge a complaint with your local data protection authority:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>EU</strong> — your national DPA (e.g. Ireland's DPC, Germany's BfDI)</li>
              <li><strong>UK</strong> — Information Commissioner's Office (ICO)</li>
            </ul>
            <p>If you are in California, under CCPA you have additional rights:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Know what personal information we collect</li>
              <li>Delete it</li>
              <li>Opt out of any "sale" (we don't sell your data)</li>
              <li>Not be discriminated against for exercising your rights</li>
            </ul>
          </Section>

          <Section title="How long we keep your data">
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="min-w-full">
                <thead><tr><TH>Data type</TH><TH>Retention</TH><TH>Reason</TH></tr></thead>
                <tbody>
                  <tr><TD>Report downloads</TD><TD>3 years</TD><TD>To measure engagement and send relevant content</TD></tr>
                  <tr><TD>Marketing opt-ins</TD><TD>Until you unsubscribe (max 5 years)</TD><TD>To send you emails you requested</TD></tr>
                  <tr><TD>Contact enquiries</TD><TD>1 year after our final response</TD><TD>To maintain records and respond to follow-ups</TD></tr>
                  <tr><TD>Website analytics (anonymised)</TD><TD>13 months</TD><TD>Standard analytics retention</TD></tr>
                </tbody>
              </table>
            </div>
            <p>If you request deletion, we remove your data within 30 days. If you've unsubscribed from marketing, we remove you from our list within 24 hours.</p>
          </Section>

          <Section title="Cookies and tracking">
            <p>We use no cookies for marketing or tracking. Our website uses:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Essential cookies only (to make the site function — login, form submission, etc.)</li>
              <li>No analytics cookies (we use privacy-respecting, cookieless analytics)</li>
              <li>No third-party tracking pixels</li>
            </ul>
            <p>When you visit our site, we do not:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Track you across the web</li>
              <li>Build a profile of your browsing behaviour</li>
              <li>Use cookies to identify you for advertising</li>
              <li>Share data with ad networks</li>
            </ul>
          </Section>

          <Section title="Data transfers outside your country">
            <p>5DBS is based in the United Kingdom. If you are in another country, your data may be transferred to and stored in the UK.</p>
            <p><strong>For EU/UK residents:</strong> If you are in the EU, your data may be transferred from the UK to the US for cloud hosting purposes. Legal basis: we rely on Standard Contractual Clauses (SCCs) as the mechanism for lawful transfer. You acknowledge that:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>The US does not have an adequacy decision</li>
              <li>US law may require disclosure of data to government agencies</li>
              <li>Your privacy protections in the US may be weaker than in the UK/EU</li>
            </ul>
            <p>If you do not consent to this transfer, please do not provide your data.</p>
          </Section>

          <Section title="Automated decision-making">
            <p>We do not use your personal data for automated decision-making that produces legal or similarly significant effects.</p>
            <p><em>Note: Product-specific decision-making (scoring, ranking, etc.) is handled by Enrich IQ and AALIGN under their separate privacy policies.</em></p>
          </Section>

          <Section title="Data subprocessors">
            <p>We use the following subprocessors to help us deliver our service:</p>
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="min-w-full">
                <thead><tr><TH>Subprocessor</TH><TH>Purpose</TH><TH>Location</TH></tr></thead>
                <tbody>
                  <tr><TD>[Analytics Provider]</TD><TD>Website analytics</TD><TD>US / EU</TD></tr>
                  <tr><TD>[Email Service]</TD><TD>Sending marketing emails</TD><TD>US / EU</TD></tr>
                  <tr><TD>[Cloud Host]</TD><TD>Hosting our website and infrastructure</TD><TD>US</TD></tr>
                </tbody>
              </table>
            </div>
            <p>For a full, updated list of subprocessors, email <a href="mailto:privacy@fivedbs.com" className="text-brand-blue font-semibold hover:underline">privacy@fivedbs.com</a>.</p>
            <p><strong>Important:</strong> We do not share your data with subprocessors unless necessary for them to help us deliver our service. All subprocessors are bound by strict data protection contracts.</p>
          </Section>

          <Section title="Data controller">
            <p><strong>Data controller:</strong> 5DBS</p>
            <p><strong>Contact:</strong> <a href="mailto:p.patne@fivedbs.com" className="text-brand-blue font-semibold hover:underline">p.patne@fivedbs.com</a></p>
            <p><strong>Address:</strong> 60 St Martins Lane, Covent Garden, London, WC2N 4JS, United Kingdom</p>
            <p>If you have a complaint about how we handle your data, email us first. If we don't resolve it to your satisfaction, you can escalate to your local data protection authority.</p>
          </Section>

          <Section title="Changes to this policy">
            <p>We may update this policy occasionally. We'll notify you of significant changes by email if you're on our marketing list, or by posting the updated policy here with a new "last updated" date.</p>
          </Section>

          {/* One-page summary card */}
          <div className="mt-16 rounded-2xl bg-navy-deep text-white p-8 shadow-mk-lg">
            <p className="text-brand-blue-light text-[11.5px] font-bold uppercase tracking-[0.18em] mb-3">One-page summary</p>
            <h3 className="text-white font-black text-[26px] leading-tight">Your data. Our promise.</h3>
            <ul className="mt-5 space-y-2.5 text-slate-300 text-[14.5px] leading-[1.7]">
              <li>• We collect your name and email only when you ask us to (download, contact, subscribe).</li>
              <li>• We never sell, share, or misuse your data.</li>
              <li>• Marketing emails are opt-in only. One click to unsubscribe anytime.</li>
              <li>• We use no tracking cookies or ad pixels.</li>
              <li>• You can request to see, correct, or delete your data anytime.</li>
            </ul>
            <p className="mt-5 text-slate-400 text-[13px]">
              <a href="mailto:privacy@fivedbs.com" className="text-brand-blue-light font-semibold hover:underline">privacy@fivedbs.com</a> · DPA available for customers
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Privacy;
